import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import Swal from "sweetalert2";
import { useTheme } from "../../../context/ThemeContext";
import { brand } from "../../../config/theme";
import { buildThemeClasses } from "../theme";
import { OriginCell } from "../components/ImportedFromTag";
import { AttachmentsList } from "../components/AttachmentsList";
import Modal from "../../../components/base/Modal";
import {
  FILTER_ALL,
  FilterSelect,
  countBy,
  matchesFilter,
  productFilterKey,
  productFilterOptions,
  sourceFilterKey,
  sourceFilterOptions,
  stageFilterOptions,
} from "../components/FilterSelect";
import {
  fetchContacts,
  updateContact,
  deleteContact,
  type Contact,
  type ContactStage,
} from "../services/contactsService";

import { DIALOG_COLORS } from "../../../components/ui";
// ===========================================================================
// Contactos — LIVE directory. A contact is a person who is no longer an active
// lead: leads are never deleted in the Bandeja, they are CONVERTED into a
// contact (which lands here). Console users can triage stage / edit / delete.
// ===========================================================================

const POLL_MS = 30000;

const STAGE_META: Record<ContactStage, { label: string; tone: string }> = {
  lead: { label: "Lead", tone: "bg-sky-500/15 text-sky-500" },
  customer: { label: "Cliente", tone: "bg-emerald-500/15 text-emerald-500" },
  inactive: { label: "Inactivo", tone: "bg-gray-400/15 text-gray-500" },
};

const STAGE_ORDER: ContactStage[] = ["lead", "customer", "inactive"];

const PRODUCT_TONE: Record<string, string> = {
  "Coding Cloud": "bg-sky-500/15 text-sky-600 dark:text-sky-400",
  Wayak: "bg-indigo-500/15 text-indigo-500",
  Nuvex: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  Movik: "bg-orange-500/15 text-orange-600 dark:text-orange-400",
};

export default function ContactosLive({ headerActions }: { headerActions?: ReactNode } = {}) {
  // `isDarkMode` sólo lo piden los componentes portados (FilterSelect).
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses();

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [productFilter, setProductFilter] = useState(FILTER_ALL);
  const [sourceFilter, setSourceFilter] = useState(FILTER_ALL);
  const [stageFilter, setStageFilter] = useState(FILTER_ALL);
  const [savingId, setSavingId] = useState<string | null>(null);
  /** The contact whose documents (HubSpot attachments) are open. */
  const [docsOf, setDocsOf] = useState<Contact | null>(null);
  const firstLoad = useRef(true);

  const load = useCallback(async () => {
    if (firstLoad.current) setLoading(true);
    setError(null);
    try {
      const page = await fetchContacts({ limit: 300 });
      setContacts(page.data);
    } catch (e) {
      const err = e as { response?: { status?: number; data?: { message?: string } }; message?: string };
      if (err.response?.status === 404) setContacts([]);
      else setError(err.response?.data?.message || err.message || "No se pudieron cargar los contactos");
    } finally {
      setLoading(false);
      firstLoad.current = false;
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return contacts.filter((c) => {
      if (!matchesFilter(productFilter, productFilterKey(c.product))) return false;
      if (!matchesFilter(sourceFilter, sourceFilterKey(c.source))) return false;
      if (!matchesFilter(stageFilter, c.stage)) return false;
      if (!q) return true;
      return [c.name, c.company, c.email, c.whatsapp, c.phone, c.role, c.product]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [contacts, query, productFilter, sourceFilter, stageFilter]);

  const productOptions = useMemo(() => productFilterOptions(contacts), [contacts]);
  const sourceOptions = useMemo(() => sourceFilterOptions(contacts), [contacts]);
  const stageOptions = useMemo(
    () => stageFilterOptions(STAGE_ORDER, STAGE_META, countBy(contacts, (c) => c.stage)),
    [contacts],
  );

  const changeStage = async (contact: Contact, stage: ContactStage) => {
    setSavingId(contact.id);
    setContacts((prev) => prev.map((c) => (c.id === contact.id ? { ...c, stage } : c)));
    try {
      await updateContact(contact.id, { stage });
    } catch {
      await load();
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo actualizar la etapa", confirmButtonColor: brand.primary });
    } finally {
      setSavingId(null);
    }
  };

  const removeContact = async (contact: Contact) => {
    const res = await Swal.fire({
      icon: "warning",
      title: "Eliminar contacto",
      text: `¿Eliminar a ${contact.name ?? "—"}? Esta acción no se puede deshacer.`,
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: DIALOG_COLORS.danger,
    });
    if (!res.isConfirmed) return;
    setContacts((prev) => prev.filter((c) => c.id !== contact.id));
    try {
      await deleteContact(contact.id);
    } catch {
      await load();
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo eliminar el contacto", confirmButtonColor: brand.primary });
    }
  };

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
              <i className="ri-user-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>Contactos</h1>
              <p className={`text-sm ${tc.textSecondary}`}>
                Personas de tu CRM · {contacts.length} contactos
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${tc.border} ${tc.cardBg}`}>
              <i className={`ri-search-line ${tc.textSecondary}`} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar contactos..."
                className={`bg-transparent text-sm outline-none ${tc.text}`}
              />
            </div>
            {headerActions}
            <button
              onClick={load}
              className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary transition-colors hover:bg-primary-400"
            >
              <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`} />
              <span className="whitespace-nowrap">Actualizar</span>
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <FilterSelect label="Producto" value={productFilter} onChange={setProductFilter} allCount={contacts.length} options={productOptions} isDarkMode={isDarkMode} />
          <FilterSelect label="Origen" value={sourceFilter} onChange={setSourceFilter} allCount={contacts.length} options={sourceOptions} isDarkMode={isDarkMode} />
          <FilterSelect label="Etapa" value={stageFilter} onChange={setStageFilter} allLabel="Todas" allCount={contacts.length} options={stageOptions} isDarkMode={isDarkMode} />
        </div>

        {/* Table */}
        <div className={`rounded-2xl border ${tc.cardBorder} ${tc.cardBg}`}>
          {loading ? (
            <div className={`p-10 text-center text-sm ${tc.textSecondary}`}>
              <i className="ri-loader-4-line mr-2 animate-spin" />
              Cargando contactos...
            </div>
          ) : error ? (
            <div className="p-10 text-center">
              <i className="ri-error-warning-line mb-2 text-3xl text-red-500" />
              <p className="text-sm text-red-500">{error}</p>
              <button onClick={load} className="mt-3 rounded-lg bg-primary-500 px-4 py-2 text-sm text-on-primary hover:bg-primary-400">
                Reintentar
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className={`p-10 text-center text-sm ${tc.textSecondary}`}>
              <i className="ri-user-line mb-2 block text-3xl" />
              {contacts.length > 0
                ? "Ningún contacto coincide con la búsqueda o los filtros."
                : "Aún no hay contactos. Al convertir un lead en la Bandeja, la persona aparecerá aquí."}
            </div>
          ) : (
            <div className="w-full overflow-x-auto p-4">
              <table className="w-full overflow-hidden rounded-xl text-xs shadow-sm">
                <thead className="bg-primary-500 text-on-primary">
                  <tr>
                    <th className="p-3 text-left font-bold">Nombre</th>
                    <th className="p-3 text-left font-bold">Empresa</th>
                    <th className="p-3 text-left font-bold">Producto</th>
                    <th className="p-3 text-left font-bold">Origen</th>
                    <th className="p-3 text-left font-bold">Contacto</th>
                    <th className="p-3 text-left font-bold">Cargo</th>
                    <th className="p-3 text-center font-bold">Etapa</th>
                    <th className="p-3 text-center font-bold">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c, i) => (
                    <tr
                      key={c.id}
                      className={`text-content ${i % 2 === 0 ? "bg-surface" : "bg-card"}`}
                    >
                      <td className="p-2 font-medium">{c.name ?? "—"}</td>
                      <td className="p-2">{c.company ?? "—"}</td>
                      <td className="p-2">
                        {c.product ? (
                          <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${PRODUCT_TONE[c.product] ?? "bg-gray-400/15 text-gray-500"}`}>
                            {c.product}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="p-2">
                        <OriginCell source={c.source} />
                      </td>
                      <td className="p-2">
                        <div className="flex flex-col">
                          {c.email && <span className="truncate">{c.email}</span>}
                          {c.whatsapp && (
                            <span className={tc.textSecondary}>
                              <i className="ri-whatsapp-line mr-1 text-emerald-500" />
                              {c.whatsapp}
                            </span>
                          )}
                          {/* Contacts converted from a phone-only lead have no
                              WhatsApp line — show the plain phone instead. */}
                          {c.phone && c.phone !== c.whatsapp && (
                            <span className={tc.textSecondary}>
                              <i className="ri-phone-line mr-1" />
                              {c.phone}
                            </span>
                          )}
                          {!c.email && !c.whatsapp && !c.phone && "—"}
                        </div>
                      </td>
                      <td className="p-2">{c.role ?? "—"}</td>
                      <td className="p-2 text-center">
                        <select
                          value={c.stage}
                          disabled={savingId === c.id}
                          onChange={(e) => changeStage(c, e.target.value as ContactStage)}
                          className="rounded-md border px-2 py-1 text-xs outline-none border-line bg-app text-content"
                        >
                          {STAGE_ORDER.map((s) => (
                            <option key={s} value={s}>{STAGE_META[s].label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="p-2">
                        <div className="flex items-center justify-center gap-1">
                          {c.whatsapp && (
                            <a
                              title="WhatsApp"
                              href={`https://wa.me/${c.whatsapp.replace(/[^\d]/g, "")}`}
                              target="_blank"
                              rel="noreferrer"
                              className="rounded-md p-1.5 text-emerald-500 hover:bg-emerald-500/10"
                            >
                              <i className="ri-whatsapp-line" />
                            </a>
                          )}
                          {!c.whatsapp && c.phone && (
                            <a
                              title="Llamar"
                              href={`tel:${c.phone.replace(/[^\d+]/g, "")}`}
                              className="rounded-md p-1.5 text-primary-500 hover:bg-primary-500/10"
                            >
                              <i className="ri-phone-line" />
                            </a>
                          )}
                          <button
                            title="Documentos"
                            aria-label={`Documentos de ${c.name ?? "contacto"}`}
                            onClick={() => setDocsOf(c)}
                            className="rounded-md p-1.5 text-primary-500 hover:bg-primary-500/10"
                          >
                            <i className="ri-attachment-2" />
                          </button>
                          <button
                            title="Eliminar"
                            onClick={() => removeContact(c)}
                            className="rounded-md p-1.5 text-red-500 hover:bg-red-500/10"
                          >
                            <i className="ri-delete-bin-line" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className={`mt-3 text-xs ${tc.textSecondary}`}>
          <i className="ri-information-line mr-1" />
          Los contactos se crean al convertir un lead desde la Bandeja. Conservan su empresa,
          producto y el historial de calificación del lead.
        </p>
      </div>

      {docsOf && (
        <Modal
          title={`Documentos — ${docsOf.name ?? "contacto"}`}
          onClose={() => setDocsOf(null)}
          themeClasses={tc}
          content={<AttachmentsList contactId={docsOf.id} textClass={tc.text} mutedClass={tc.textSecondary} borderClass={tc.border} />}
        />
      )}
    </div>
  );
}
